import { useEffect } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { createEnumParam, StringParam, useQueryParam } from "use-query-params";

import {
  calendarNext,
  calendarPrev,
  companyIdSelector,
  getCalendarModeForDateUnit,
  getDateUnitForCalendarMode,
  getStoredTopCalendarData,
  INITIAL_CALENDAR_DROPDOWN_VALUE,
  Log,
  storeTopCalendarData,
  systemSettingsDtoAtom,
  TOP_CALENDAR_DATE1_QUERY_NAME,
  TOP_CALENDAR_DATE2_QUERY_NAME,
  TOP_CALENDAR_MODE_QUERY_NAME,
  topCalendarDatesAtomFamily,
  TopCalendarMode,
  topCalendarModeAtomFamily,
  topCalendarModeSF,
  useTimezone,
  weekendInfoSelector,
  type DatesUnit,
  type RydeUiUnit,
} from "@ryde/shared";

import { useTopCalendarItems } from "./use-top-calendar.items";

export const useTopCalendar = (
  entity: RydeUiUnit,
  defaultMode?: TopCalendarMode,
) => {
  const companyId = useRecoilValue(companyIdSelector);
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const canSetupInitialDates = Boolean(companyId && systemSettings);
  const setCalendarMode = useSetRecoilState(topCalendarModeAtomFamily(entity));
  const calendarMode = useRecoilValue(topCalendarModeSF(entity));
  const [weekendStart, weekendDuration] = useRecoilValue(weekendInfoSelector);
  const { timezone } = useTimezone();
  const { dropdownItems } = useTopCalendarItems({
    weekendStart,
    weekendDuration,
  });
  const [topCalendarDates, setTopCalendarDates] = useRecoilState(
    topCalendarDatesAtomFamily(entity),
  );
  const [dateQsParam1, setDateQsParam1] = useQueryParam(
    TOP_CALENDAR_DATE1_QUERY_NAME,
    StringParam,
  );
  const [dateQsParam2, setDateQsParam2] = useQueryParam(
    TOP_CALENDAR_DATE2_QUERY_NAME,
    StringParam,
  );
  const [topCalendarMode, setTopCalendarMode] = useQueryParam(
    TOP_CALENDAR_MODE_QUERY_NAME,
    createEnumParam<TopCalendarMode>(Object.values(TopCalendarMode)),
  );

  const pushState = useEvent(({ date1, date2 }: DatesUnit): void => {
    setDateQsParam1(date1);
    setDateQsParam2(date2);
    setTopCalendarMode(calendarMode);
  });

  useEffect(() => {
    if (topCalendarDates) {
      pushState(topCalendarDates);
    }
  }, [topCalendarDates, pushState]);

  const changeCalendarDates = useEvent(
    (unit: DatesUnit, forceCalendarMode?: TopCalendarMode) => {
      if (!companyId) {
        return;
      }

      // Keep the previous object when the range has not actually changed. Recoil does not notify
      // subscribers for a reference-identical value, so this stops every effect that depends on this
      // atom from refetching when a write resolves to the same two dates - re-picking the range that
      // is already active, or a mode change that lands on the same days.
      //
      // This covers the calendar UI, which is where the churn came from, but NOT every writer: the
      // global ride-id search (`use-rides-navigation`) and its clear-up (`use-pre-global-search-
      // calendar`) set the atom directly. A consumer that must not refetch on an equal range still
      // has to compare values - see `buildRidesFetchKey`.
      setTopCalendarDates(prev =>
        prev?.date1 === unit.date1 && prev?.date2 === unit.date2 ? prev : unit,
      );

      const newPossibleMode =
        forceCalendarMode ||
        getCalendarModeForDateUnit({
          dates: unit,
          weekendDuration,
          weekendStart,
          timezone,
        });

      if (newPossibleMode) {
        Log.debug("Auto-set mode to:", newPossibleMode);
        setCalendarMode(newPossibleMode);
      } else {
        Log.debug("No matching mode found, auto-set mode to CUSTOM");
        setCalendarMode(TopCalendarMode.CUSTOM);
      }

      storeTopCalendarData({
        companyId,
        entity,
        dates: unit,
        mode: newPossibleMode ?? TopCalendarMode.CUSTOM,
      });
    },
  );

  const changeCalendarMode = useEvent((mode: TopCalendarMode) => {
    if (mode !== TopCalendarMode.CUSTOM) {
      const datesUnit = getDateUnitForCalendarMode({
        mode,
        weekendStart,
        weekendDuration,
        timezone,
      });
      changeCalendarDates(datesUnit, mode);
    } else {
      setCalendarMode(mode);
    }
  });

  /*
   * First check query string "mode" param
   * Second check query string dates params if "mode" custom
   * Third check localstorage
   * Fourth set default initial by hardcoded("Today" usually)
   * */
  const setupInitialDates = useEvent((): void => {
    if (topCalendarDates || !companyId || !systemSettings) {
      return;
    }

    const isTopCalendarMode =
      topCalendarMode !== undefined &&
      topCalendarMode !== null &&
      topCalendarMode in TopCalendarMode;

    if (isTopCalendarMode && topCalendarMode !== TopCalendarMode.CUSTOM) {
      const datesFromQsMode = getDateUnitForCalendarMode({
        mode: topCalendarMode,
        weekendStart,
        weekendDuration,
        timezone,
      });
      changeCalendarDates(datesFromQsMode, topCalendarMode);
    } else if (
      isTopCalendarMode &&
      topCalendarMode === TopCalendarMode.CUSTOM &&
      dateQsParam1 &&
      dateQsParam2
    ) {
      const datesQS: DatesUnit = {
        date1: dateQsParam1 as TDateISO,
        date2: dateQsParam2 as TDateISO,
      };
      changeCalendarDates(datesQS);

      storeTopCalendarData({
        companyId,
        entity,
        dates: datesQS,
        mode: topCalendarMode,
      });
    } else {
      const storageDates = getStoredTopCalendarData(
        companyId,
        entity,
        timezone,
      );

      if (storageDates) {
        if (storageDates.mode === TopCalendarMode.CUSTOM) {
          changeCalendarDates(storageDates.dates);
        } else {
          changeCalendarDates(
            getDateUnitForCalendarMode({
              mode: storageDates.mode,
              weekendStart,
              weekendDuration,
              timezone,
            }),
            storageDates.mode,
          );
        }
      } else if (defaultMode) {
        changeCalendarDates(
          getDateUnitForCalendarMode({
            mode: defaultMode,
            weekendStart,
            weekendDuration,
            timezone,
          }),
          defaultMode,
        );
      } else {
        const datesDefault = getDateUnitForCalendarMode({
          mode: INITIAL_CALENDAR_DROPDOWN_VALUE,
          weekendDuration,
          weekendStart,
          timezone,
        });
        changeCalendarDates(datesDefault);
      }
    }
  });

  const navigateNext = useEvent(() => {
    if (topCalendarDates) {
      const nextUnit = calendarNext(calendarMode, topCalendarDates, timezone);
      changeCalendarDates(nextUnit);
      Log.debug("Navigating next:", nextUnit);
    }
  });

  const navigatePrev = useEvent(() => {
    if (topCalendarDates) {
      const prevUnit = calendarPrev(calendarMode, topCalendarDates, timezone);
      changeCalendarDates(prevUnit);
      Log.debug("Navigating right to:", prevUnit);
    }
  });
  const reInitTopCalendarUnitFromLocationQueryString = useEvent(() => {
    setTopCalendarDates(undefined); // triggers effect in TopHeaderCalendar
  });

  return {
    calendarMode,
    changeCalendarMode,
    navigateNext,
    navigatePrev,
    dropdownItems,
    setupInitialDates,
    canSetupInitialDates,
    changeCalendarDates,
    reInitTopCalendarUnitFromLocationQueryString,
  };
};
